/**
 * Coalesces concurrent GET /cart.js calls into a single real network request.
 *
 * Five separate scripts (kozler-cart-drawer.js, fzk-cart-rules.js, tbf-cart-qty.js,
 * extended-access-preload.js, fitaz-wlr-pdp-analytics.js) each independently fetch
 * /cart.js in reaction to the same cart-mutation events, which measured as ~20
 * duplicate GET /cart.js requests firing from a single Add to Cart click.
 *
 * This is a pure network-layer shim, not a rewrite of any of that cart logic: every
 * caller still calls fetch('/cart.js') and gets back an ordinary, independently
 * readable Response (via .clone()) exactly as before. Only the actual request over
 * the wire is deduped. Nothing here changes what any of the five scripts decide to
 * do with the response.
 *
 * Loaded first, at the very top of <head> (see layout/theme.liquid), so it installs
 * before any other script wraps window.fetch — being innermost (closest to the real
 * network call) is what lets it dedupe regardless of how many outer wrappers each
 * independently call fetch('/cart.js').
 *
 * Scope is deliberately narrow: only bare GET requests to the /cart.js pathname
 * (ignoring query strings — a cache-busted ?r=... call is still logically the same
 * read) with no custom `signal`, so aborting one caller can't cancel another's
 * request. Everything else (add/update/change, any other path, any other method,
 * a caller that passes its own AbortSignal) passes straight through untouched.
 */
(function () {
  if (window.__cartFetchDedupeInstalled) return;
  window.__cartFetchDedupeInstalled = true;

  var nativeFetch = window.fetch.bind(window);
  var POST_RESOLVE_CACHE_MS = 100;
  var inFlight = null;
  var inFlightExpiresAt = 0;

  var isPlainCartJsGet = function (input, init) {
    var method = (init && init.method) || (input && input.method) || 'GET';
    if (String(method).toUpperCase() !== 'GET') return false;
    if (init && init.signal) return false;
    if (input && typeof input === 'object' && input.signal) return false;

    var url = typeof input === 'string' ? input : (input && input.url) || '';
    try {
      var pathname = new URL(url, window.location.origin).pathname;
      return pathname === '/cart.js';
    } catch (error) {
      return false;
    }
  };

  window.fetch = function (input, init) {
    if (!isPlainCartJsGet(input, init)) {
      return nativeFetch(input, init);
    }

    var now = Date.now();
    if (inFlight && now < inFlightExpiresAt) {
      return inFlight.then(function (response) {
        return response.clone();
      });
    }

    inFlight = nativeFetch(input, init).then(
      function (response) {
        inFlightExpiresAt = Date.now() + POST_RESOLVE_CACHE_MS;
        return response;
      },
      function (error) {
        // Don't let a failed request poison the cache for the next real attempt.
        inFlight = null;
        inFlightExpiresAt = 0;
        throw error;
      }
    );

    return inFlight.then(function (response) {
      return response.clone();
    });
  };
})();
